import { createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  memo,
  startTransition,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react'
import type { CellContext } from '@tanstack/react-table'
import { PageHeader } from '@/components/layout/page-header'
import { ViewTabs } from '@/components/custom/view-tabs'
import { DataTable } from '@/components/custom/datatable'
import { CellBadge } from '@/components/custom/datatable/cells'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { AddCompanyModal } from '@/components/custom/companies/add-company-modal'
import { CompanySidePeek } from '@/components/custom/companies/company-side-peek'
import { PermissionGate } from '@/components/guards/permission-gate'
import { useDataTable } from '@/hooks/use-datatable'
import { companiesKeys } from '@/hooks/use-companies'
import { useFieldDefinitions } from '@/hooks/use-field-definitions'
import { useViewActions } from '@/hooks/use-view-actions'
import { useView } from '@/hooks/use-views'
import { useIsMobile } from '@/hooks/use-mobile'
import { useUnsavedChangesBlocker } from '@/hooks/use-unsaved-changes-blocker'
import { useUserStore, selectUser } from '@/stores/user.store'
import type {
  Company,
  CreateViewColumn,
  CreateViewFilter,
  CreateViewSort,
  FilterOperator,
  Filter,
} from '@/types/api'
import { columnIdToFieldName } from '@/components/custom/datatable/types'
import { parseSearchParams } from '@/hooks/use-url-sync'

type CompaniesSearch = {
  companyId?: string
  filters?: string
  sorts?: string
  q?: string
}

function getStringArrayFilterValue(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value === 'string') return [value]
  return []
}

function getSingleFundContextId(filters: Filter[]): string | null {
  const fundFilter = filters.find((f) => f.field === 'funds')
  if (!fundFilter) return null
  const fundIds = getStringArrayFilterValue(fundFilter.value as unknown)
  if (fundIds.length !== 1) return null
  return fundIds[0]
}

function getFundScopedStatus(
  company: Company,
  fundContextId: string
): string | null {
  const match = (company.funds ?? []).find(
    (f) => f.id === fundContextId || f.abbreviation === fundContextId
  )
  return (match?.investmentStatus as unknown as string | undefined) ?? null
}

export const Route = createFileRoute('/_authenticated/companies/views/$viewId')(
  {
    component: CompaniesViewPage,
    validateSearch: (search: Record<string, unknown>): CompaniesSearch => {
      return {
        companyId:
          typeof search.companyId === 'string' ? search.companyId : undefined,
        filters:
          typeof search.filters === 'string' ? search.filters : undefined,
        sorts: typeof search.sorts === 'string' ? search.sorts : undefined,
        q: typeof search.q === 'string' ? search.q : undefined,
      }
    },
  }
)

function CompaniesViewPage() {
  const { viewId } = Route.useParams()
  const { companyId, filters: urlFilters } = Route.useSearch()
  const navigate = Route.useNavigate()

  const fundContextId = useMemo(() => {
    const parsed = parseSearchParams({ filters: urlFilters })
    return getSingleFundContextId(parsed.filters)
  }, [urlFilters])

  const handleCloseSidePeek = useCallback(() => {
    startTransition(() => {
      navigate({
        search: (prev) => ({
          ...prev,
          companyId: undefined,
        }),
      })
    })
  }, [navigate])

  const handleExpandSidePeek = useCallback(() => {
    if (!companyId) return

    startTransition(() => {
      navigate({
        to: '/companies/$companyId',
        params: { companyId },
        search: { fromViewId: viewId },
      })
    })
  }, [companyId, navigate, viewId])

  return (
    <div className="flex h-full min-w-0 flex-col gap-6">
      <CompaniesViewTable viewId={viewId} />

      {/* Company side-peek panel */}
      {companyId && (
        <CompanySidePeek
          companyId={companyId}
          fundContextId={fundContextId}
          onClose={handleCloseSidePeek}
          onExpand={handleExpandSidePeek}
        />
      )}
    </div>
  )
}

const CompaniesViewTable = memo(function CompaniesViewTable({
  viewId,
}: {
  viewId: string
}) {
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()

  // Get current user for ownership check
  const currentUser = useUserStore(selectUser)

  // Fetch the view to check ownership
  const { data: view } = useView(viewId)

  // Get field definitions for filter options
  const { fieldDefinitions } = useFieldDefinitions('COMPANY')

  // Use the datatable hook for data management
  const {
    data,
    columns,
    isLoading,
    isLoadingMore,
    isError,
    hasMore,
    fetchMore,
    sorting,
    onSortChange,
    onSortSelect,
    onSortUpdate,
    onSortReorder,
    clearSorts,
    columnOrder,
    onColumnOrderChange,
    columnSizing,
    onColumnSizingChange,
    columnVisibilityItems,
    onColumnVisibilityChange,
    onBatchColumnVisibilityChange,
    searchTerm,
    onSearchChange,
    filters,
    onFilterChange,
    hasUnsavedChanges,
    revertAllChanges,
    getCurrentColumns: getColumnsForSave,
    refetch,
  } = useDataTable({
    entityType: 'COMPANY',
    viewId,
    isColumnAvailable: (fieldName, ctx) => {
      if (fieldName !== 'status') return true
      return getSingleFundContextId(ctx.filters) !== null
    },
  })

  const fundContextId = useMemo(
    () => getSingleFundContextId(filters),
    [filters]
  )

  // Auto-remove status filter when fund context goes away
  const prevFundContextId = useRef(fundContextId)
  useEffect(() => {
    if (prevFundContextId.current && !fundContextId) {
      // Fund context was removed — strip the status filter
      const hasStatusFilter = filters.some((f) => f.field === 'status')
      if (hasStatusFilter) {
        onFilterChange(filters.filter((f) => f.field !== 'status'))
      }
    }
    prevFundContextId.current = fundContextId
  }, [fundContextId, filters, onFilterChange])

  const fundScopedColumns = useMemo(() => {
    if (!fundContextId) return columns
    return columns.map((col) => {
      if (col.fieldName !== 'status') return col
      return {
        ...col,
        accessorFn: (row: Company) => getFundScopedStatus(row, fundContextId),
        cell: (info: CellContext<Company, unknown>) => {
          const status = getFundScopedStatus(info.row.original, fundContextId)
          return <CellBadge value={status} />
        },
      }
    })
  }, [columns, fundContextId])

  // View actions for save
  const { handleSaveView, isUpdating } = useViewActions({
    entityType: 'COMPANY',
    viewId,
  })

  // Check if user can update the view (only owners can update)
  const canUpdateView = useMemo(() => {
    if (!view || !currentUser) return false
    // User owns the view
    return view.createdByUserId === currentUser.id
  }, [view, currentUser])

  // Build filter options from field definitions
  // Status is only filterable when a single fund is selected (fund-scoped status)
  const filtersList = useMemo(() => {
    return fieldDefinitions
      .filter((field) => {
        if (!field.isFilterable) return false
        if (field.fieldName === 'status') return !!fundContextId
        return true
      })
      .map((field) => ({
        key: field.fieldName,
        label: field.displayName,
        type: field.dataType,
        dataType: field.dataType,
        listContent: field.options,
      }))
  }, [fieldDefinitions, fundContextId])

  // Build sort options from sortable field definitions
  const sortsList = useMemo(() => {
    return fieldDefinitions
      .filter((field) => field.isSortable)
      .map((field) => ({
        key: field.fieldName,
        label: field.displayName,
        type: field.dataType,
      }))
  }, [fieldDefinitions])

  // Convert filters to record format for ViewTabs
  const filtersRecord = useMemo(() => {
    const record: Record<string, string[]> = {}
    for (const filter of filters) {
      const value = filter.value
      if (Array.isArray(value)) {
        record[filter.field] = value.map(String)
      } else if (value !== undefined && value !== null) {
        record[filter.field] = [String(value)]
      } else {
        // Include filter key even with empty values (for pending selection UI)
        record[filter.field] = []
      }
    }
    return record
  }, [filters])

  // Extract operators from filters for ViewTabs
  const filtersOperators = useMemo(() => {
    const record: Record<string, FilterOperator> = {}
    for (const filter of filters) {
      record[filter.field] = filter.op
    }
    return record
  }, [filters])

  // Handle filter change from ViewTabs
  const handleFilterChange = useCallback(
    (filterKey: string, values: string[] | undefined) => {
      const existingFilterIndex = filters.findIndex(
        (f) => f.field === filterKey
      )

      // If removing the filter (values is undefined)
      if (values === undefined) {
        const newFilters = filters.filter((f) => f.field !== filterKey)
        onFilterChange(newFilters)
        return
      }

      // If updating existing filter, preserve its position
      if (existingFilterIndex !== -1) {
        const newFilters = filters.map((f, index) =>
          index === existingFilterIndex ? { ...f, value: values } : f
        )
        onFilterChange(newFilters)
        return
      }

      // If adding new filter, append to end
      const newFilters = [
        ...filters,
        {
          field: filterKey,
          op: 'in' as const,
          value: values,
          logicalOp: 'AND' as const,
        },
      ]
      onFilterChange(newFilters)
    },
    [filters, onFilterChange]
  )

  // Handle filter operator change from ViewTabs
  const handleFilterOperatorChange = useCallback(
    (filterKey: string, operator: FilterOperator) => {
      const newFilters = filters.map((f) =>
        f.field === filterKey ? { ...f, op: operator } : f
      )
      onFilterChange(newFilters)
    },
    [filters, onFilterChange]
  )

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    onFilterChange([])
  }, [onFilterChange])

  // Handle row click
  const handleRowClick = useCallback(
    (row: Company) => {
      queryClient.setQueryData(companiesKeys.detail(row.id), row)
      startTransition(() => {
        navigate({
          // Keep any existing query params (filters/sorts/q, etc.)
          search: (prev) => ({
            ...prev,
            companyId: row.id,
          }),
        })
      })
    },
    [navigate, queryClient]
  )

  // Get current columns for creating new views
  const getCurrentColumns = useCallback((): CreateViewColumn[] => {
    if (!columnVisibilityItems || !columnOrder) return []

    // Create a map for quick visibility lookup
    const visibilityMap = new Map(
      columnVisibilityItems.map((col) => [col.id, col.isVisible])
    )

    // Return visible columns in order
    return columnOrder
      .filter((colId) => visibilityMap.get(colId) !== false)
      .map((colId, index) => ({
        fieldName: columnIdToFieldName(colId),
        displayOrder: index,
        isVisible: true,
        width: columnSizing[colId],
      }))
  }, [columnVisibilityItems, columnOrder, columnSizing])

  // Get current filters for creating new views
  // Note: Backend handles field name transformation (e.g., primaryOwner -> primaryOwner.name)
  const getCurrentFilters = useCallback((): CreateViewFilter[] => {
    if (!filters.length) return []

    return filters.map((f, index) => ({
      fieldName: f.field,
      operator: f.op === 'in' ? 'IN' : 'EQUALS',
      value: f.value,
      filterOrder: index,
    }))
  }, [filters])

  // Get current sorts for creating new views
  const getCurrentSorts = useCallback((): CreateViewSort[] => {
    if (!sorting.length) return []

    return sorting.map((s, index) => ({
      fieldName: columnIdToFieldName(s.id),
      direction: s.desc ? 'DESC' : 'ASC',
      sortOrder: index,
    }))
  }, [sorting])

  // Handle save view with current filter/sort/column state
  const handleSave = useCallback(() => {
    const currentFilters = getCurrentFilters()
    const currentSorts = getCurrentSorts()
    const currentColumns = getColumnsForSave()

    handleSaveView({
      filters: currentFilters,
      sorts: currentSorts,
      columns: currentColumns,
    })
  }, [getCurrentFilters, getCurrentSorts, getColumnsForSave, handleSaveView])

  return (
    <>
      {/* Page header (hidden on mobile - ViewTabs shows title) */}
      <div className="flex items-center justify-between pr-4 md:pr-8">
        {isMobile ? (
          <Typography variant="h1" className="sr-only">
            Companies
          </Typography>
        ) : (
          <PageHeader title="Companies" useSeparator={false} />
        )}
        <PermissionGate adminOnly>
          <NewCompanyButton />
        </PermissionGate>
      </div>

      {/* View tabs with search and filters */}
      <div className="pr-4 md:pr-8">
        <ViewTabs
          entityType="COMPANY"
          basePath="/companies/views"
          pageTitle="Companies"
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          filtersList={filtersList}
          filters={filtersRecord}
          filterOperators={filtersOperators}
          onFilterChange={handleFilterChange}
          onFilterOperatorChange={handleFilterOperatorChange}
          clearAllFilters={clearAllFilters}
          totalMatches={data.length}
          columnVisibilityItems={columnVisibilityItems}
          onColumnVisibilityChange={onColumnVisibilityChange}
          onBatchColumnVisibilityChange={onBatchColumnVisibilityChange}
          onColumnOrderChange={onColumnOrderChange}
          sorting={sorting}
          sortsList={sortsList}
          onSortSelect={onSortSelect}
          onSortUpdate={onSortUpdate}
          onSortReorder={onSortReorder}
          clearSorts={clearSorts}
          getCurrentColumns={getCurrentColumns}
          getCurrentFilters={getCurrentFilters}
          getCurrentSorts={getCurrentSorts}
          hasUnsavedChanges={hasUnsavedChanges}
          onSaveView={handleSave}
          canUpdateView={canUpdateView}
          isSaving={isUpdating}
          onRevertToSaved={revertAllChanges}
        />
      </div>

      {/* Companies DataTable */}
      <DataTable
        data={data}
        columns={fundScopedColumns}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        isError={isError}
        hasMore={hasMore}
        sorting={sorting}
        onSortChange={onSortChange}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onLoadMore={fetchMore}
        onRefresh={refetch}
        onRowClick={handleRowClick}
        searchTerm={searchTerm}
        enableColumnReorder
        enableColumnResize
        className="flex-1"
      />

      {/* Unsaved changes navigation blocker */}
      <UnsavedChangesBlockerController
        hasUnsavedChanges={hasUnsavedChanges}
        onLeave={revertAllChanges}
      />
    </>
  )
})

function NewCompanyButton() {
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false)

  return (
    <>
      <Button onClick={() => setIsAddCompanyModalOpen(true)}>
        + New Company
      </Button>
      {isAddCompanyModalOpen && (
        <AddCompanyModal
          open={isAddCompanyModalOpen}
          onOpenChange={setIsAddCompanyModalOpen}
        />
      )}
    </>
  )
}

const UnsavedChangesBlockerController = memo(
  function UnsavedChangesBlockerController({
    hasUnsavedChanges,
    onLeave,
  }: {
    hasUnsavedChanges: boolean
    onLeave: () => void
  }) {
    const { UnsavedChangesBlocker } = useUnsavedChangesBlocker({
      hasUnsavedChanges,
      onLeave,
    })

    return <UnsavedChangesBlocker />
  }
)
